import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, memo } from "react";
import { defaultGetItemLabel, defaultGetItemValue } from "../utils";
import Autocomplete from "./DployAutocompleteMui";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { useThemeProps } from "@ploy-ui/tenants";
import { DployTextField } from "../DployTextField";
import { useIntl, defineMessages } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { Popper } from "@material-ui/core";
const messages = defineMessages({
    placeholder: {
        id: "core.dploy-autocomplete.placeholder",
        defaultMessage: "Select...",
        description: "Default selection message"
    },
    noOptionsMessage: {
        id: "core.dploy-autocomplete.no-options",
        defaultMessage: "No options match your search",
        description: "No options availible message"
    }
});
const useStyles = makeStyles({
    focused: (props) => ({
        minWidth: props.minWidthFocused ?? undefined
    }),
    root: (props) => ({
        pointerEvents: props.readonly ? "none" : "inherit",
        width: props.fullWidth ? "100%" : undefined
    }),
    groupLabel: {
        fontWeight: 700
    },
    inputRoot: (props) => ({
        cursor: props.searchable === false ? "pointer" : "text"
    }),
    input: (props) => ({
        cursor: props.searchable === false ? "pointer !important" : "text"
    })
}, { name: "DployAutocomplete" });
const emptyArray = [];
function DployAutocompleteInternal(props) {
    const { id, className, openOnFocus = false, autoComplete, autoHighlight = true, autoSelect, disableCloseOnSelect, items = emptyArray, getItemLabel = defaultGetItemLabel, getItemValue = defaultGetItemValue, getItemSuggestions, debounceTimeout = 200, getCreatedItem, // TODO?
    removeNoOptionsMessage, multiple, searchable = true, clearable, disableClearable = !clearable, manual, creatable, // TODO?
    onSelectItem, readonly, pending, minSearch = 1, value, onChange, inputValue: propsInputValue, onInputChange: propsOnInputChange, variant = "select", disabled, fullWidth, renderTags, renderOption, minWidthFocused, AutocompleteProps, filterOptions, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, ...textFieldProps } = useThemeProps(props, "DployAutocomplete");
    const classes = useStyles(props);
    const [options, setOptions] = React.useState(items);
    const [inputValue, setInputValue] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const intl = useIntl();
    const placeholder = textFieldProps.placeholder || intl.formatMessage(messages.placeholder);
    const noOptionsMessage = removeNoOptionsMessage
        ? null
        : intl.formatMessage(messages.noOptionsMessage);
    const searchValue = propsInputValue !== undefined ? propsInputValue : inputValue;
    const [debounce] = useDebouncedCallback(f => f(), debounceTimeout);
    useEffect(() => {
        if (!getItemSuggestions || searchValue.length < minSearch) {
            setOptions(items);
            setLoading(false);
            return;
        }
        let active = true;
        debounce(async () => {
            if (!active)
                return;
            setLoading(true);
            try {
                const suggestions = await getItemSuggestions(searchValue);
                if (active && suggestions) {
                    setOptions(suggestions);
                }
            }
            finally {
                if (active)
                    setLoading(false);
            }
        });
        return () => {
            active = false;
        };
    }, [searchValue, getItemSuggestions, minSearch, items, debounce]);
    const handleInputChange = useCallback((e, value, ...rest) => {
        setInputValue(value);
        if (propsOnInputChange)
            propsOnInputChange(e, value, ...rest);
    }, [propsOnInputChange]);
    const isGrouped = useMemo(() => options.some(x => {
        const label = getItemLabel(x);
        return label && label.toString().includes("|");
    }), [options, getItemLabel]);
    const groupBy = useCallback((item) => {
        const label = getItemLabel(item);
        const [, secondary] = label != null ? label.toString().split("|", 2) : [];
        return secondary;
    }, [getItemLabel]);
    const getOptionLabel = useCallback((item) => {
        const label = getItemLabel(item);
        const [primary] = label != null ? label.toString().split("|", 1) : [];
        return primary;
    }, [getItemLabel]);
    const selectedValue = useMemo(() => {
        if (value == null || value === undefined || value === "")
            return multiple ? [] : null;
        if (!getItemValue)
            return value;
        if (multiple) {
            const multipleValue = value;
            return options.filter(x => 
            // eslint-disable-next-line eqeqeq
            multipleValue?.some(
            // eslint-disable-next-line eqeqeq
            v => getItemValue(x) == getItemValue(v) || getItemValue(x) == v) // == allows to compare string with int, which is necassary here.
            );
        }
        return (options.find(x => getItemValue(x)?.toString() === getItemValue(value)?.toString()) || null);
    }, [getItemValue, multiple, options, value]);
    var inputValueToRender = useMemo(() => {
        if (propsInputValue)
            return propsInputValue;
        else if (inputValue)
            return inputValue;
        else if (!selectedValue && typeof value === "string")
            return value;
        else
            return "";
    }, [inputValue, propsInputValue, selectedValue, value]);
    return (_jsx(Autocomplete, { id: id, openOnFocus: !readonly && openOnFocus, forcePopupIcon: readonly ? false : undefined, autoComplete: autoComplete, autoHighlight: autoHighlight, autoSelect: autoSelect && !multiple, disableCloseOnSelect: disableCloseOnSelect, value: selectedValue, options: options, onChange: onChange, getOptionLabel: getOptionLabel, getOptionSelected: (option, value) => getItemValue
            ? getItemValue(option) === getItemValue(value)
            : option === value, groupBy: isGrouped ? groupBy : undefined, noOptionsText: noOptionsMessage, freeSolo: manual, multiple: multiple, loading: loading || pending, disableClearable: disableClearable || readonly, filterOptions: filterOptions, disabled: disabled, onInputChange: handleInputChange, inputValue: inputValueToRender, 
        // inputValue={
        // 	propsInputValue !== undefined
        // 		? propsInputValue
        // 		: !selectedValue &&
        // 		  getItemSuggestions &&
        // 		  items.length === 0 &&
        // 		  disabled &&
        // 		  !multiple &&
        // 		  typeof value === "string"
        // 		? value
        // 		: inputValue
        // }
        renderTags: renderTags, renderOption: renderOption, renderInput: params => (_jsx(DployTextField, { className: className, ...textFieldProps, variant: variant, placeholder: placeholder, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, ...params, inputProps: {
                ...textFieldProps.inputProps,
                ...params.inputProps,
                readOnly: !searchable || readonly,
                tabIndex: readonly ? -1 : undefined
            }, InputProps: {
                ...textFieldProps.InputProps,
                ...params.InputProps,
                startAdornment: (_jsxs(_Fragment, { children: [params.InputProps.startAdornment, textFieldProps.InputProps?.startAdornment] })),
                endAdornment: (_jsxs(_Fragment, { children: [pending || loading ? (_jsx(CircularProgress, { color: "inherit", size: 20 })) : null, textFieldProps.InputProps?.endAdornment, params.InputProps.endAdornment] }))
            }, fullWidth: true })), PopperComponent: removeNoOptionsMessage &&
            (inputValue.length < minSearch || !loading) &&
            options.length < 1
            ? NoOptionsPopper
            : minWidthFocused
                ? ({ style, ...rest }) => (_jsx(Popper, { style: { width: minWidthFocused, minWidth: "fit-content" }, ...rest }))
                : undefined //use default popper
        , ...AutocompleteProps, classes: {
            focused: classes.focused,
            root: classes.root,
            groupLabel: classes.groupLabel,
            inputRoot: classes.inputRoot,
            input: classes.input,
            ...(AutocompleteProps && AutocompleteProps.classes)
        } }));
}
const NoOptionsPopper = props => {
    return _jsx(Popper, { ...props, style: { visibility: "hidden" } });
};
DployAutocompleteInternal.displayName = "DployAutocomplete";
export const DployAutocomplete = memo(DployAutocompleteInternal);
//# sourceMappingURL=DployAutocomplete.js.map